import React, { useCallback, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Trash2, X, Loader2, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

/**
 * Global bulk-select + soft-delete hook (Phase 84.15, Founder-only as of Phase 96).
 *
 *   const { canBulk, selected, isSelected, toggle, clear, toggleAll,
 *           RowCheckbox, HeaderCheckbox, ActionBar, ConfirmDialog } = useBulkSelection({
 *     collection: "finance_expenses",
 *     ids: rows.map(r => r.id),
 *     onDone: reload,
 *   });
 *
 * Rules:
 *  - Bulk actions are Founder-only. `canBulk` is false for everyone else
 *    (including Admin) so the whole feature stays invisible.
 *  - Delete opens a custom orange-accented `ConfirmDialog` (no native
 *    browser confirm). Default is a SOFT delete → server sets `deleted_at`,
 *    paired with a 30s "Undo" toast. A Founder-only "Permanently delete
 *    (cannot be undone)" checkbox in the dialog switches to a hard delete
 *    (`/bulk-delete-permanent`) with no Undo.
 *  - `ActionBar` is a floating pill fixed to the bottom-center; `ConfirmDialog`
 *    must be rendered alongside it once per page.
 *  - `RowCheckbox` + `HeaderCheckbox` are Shadcn `<Checkbox>` wrappers with
 *    their onCheckedChange wired to the internal Set state.
 */
export default function useBulkSelection({ collection, ids, onDone }) {
  const { user } = useAuth();
  const canBulk = user?.role === "founder";

  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [permanent, setPermanent] = useState(false);
  // Feb 2026 — Shift+Click range-select: remembers the last row a checkbox
  // was clicked on (not shift-clicked) so a subsequent Shift+Click can
  // select every row in between, matching the standard spreadsheet/Gmail UX.
  const [anchorId, setAnchorId] = useState(null);
  // Oct 2026 — `ids` is read through a ref so `toggle` / `RowCheckbox` keep a
  // stable identity when the row list is re-created (every inline edit does
  // that); otherwise every memo'd ledger row re-rendered on each commit.
  const idsRef = useRef(ids);
  idsRef.current = ids;

  const isSelected = useCallback((id) => selected.has(id), [selected]);

  const toggle = useCallback((id, opts = {}) => {
    const ids = idsRef.current;
    setSelected((prev) => {
      const next = new Set(prev);
      if (opts.shiftKey && anchorId != null && ids.includes(anchorId) && ids.includes(id)) {
        const startIdx = ids.indexOf(anchorId);
        const endIdx = ids.indexOf(id);
        const [lo, hi] = startIdx <= endIdx ? [startIdx, endIdx] : [endIdx, startIdx];
        // Mirror the clicked row's NEW state across the whole in-between range.
        const shouldSelect = !next.has(id);
        for (let i = lo; i <= hi; i++) {
          if (shouldSelect) next.add(ids[i]); else next.delete(ids[i]);
        }
      } else {
        if (next.has(id)) next.delete(id); else next.add(id);
      }
      return next;
    });
    // Shift+Click extends from the existing anchor and never itself becomes
    // the new anchor — so Shift+Click, Shift+Click again keeps growing/
    // shrinking the same range instead of re-anchoring every time.
    if (!opts.shiftKey) setAnchorId(id);
  }, [anchorId]);

  const clear = useCallback(() => { setSelected(new Set()); setAnchorId(null); }, []);

  const allSelected = useMemo(() => {
    if (!ids || ids.length === 0) return false;
    return ids.every((id) => selected.has(id));
  }, [ids, selected]);

  const toggleAll = useCallback(() => {
    setSelected((prev) => {
      const next = new Set(prev);
      const shouldSelect = !ids.every((id) => next.has(id));
      if (shouldSelect) ids.forEach((id) => next.add(id));
      else ids.forEach((id) => next.delete(id));
      return next;
    });
  }, [ids]);

  const openConfirm = useCallback(() => {
    if (selected.size === 0) return;
    setPermanent(false);
    setConfirmOpen(true);
  }, [selected]);

  const runDelete = useCallback(async () => {
    const ids_arr = Array.from(selected);
    if (ids_arr.length === 0) return;
    const hard = permanent && canBulk;
    setBusy(true);
    try {
      const { data } = await api.post(hard ? "/bulk-delete-permanent" : "/bulk-delete", { collection, ids: ids_arr });
      const deleted = data.deleted || 0;
      clear();
      setConfirmOpen(false);
      if (hard) {
        toast.success(`Permanently deleted ${deleted} row${deleted === 1 ? "" : "s"}`);
      } else {
        // Fire the 30-second Undo toast (Sonner default duration override).
        toast.success(`Deleted ${deleted} row${deleted === 1 ? "" : "s"}`, {
          duration: 30000,
          action: {
            label: "Undo",
            onClick: async () => {
              try {
                const { data: rd } = await api.post("/bulk-restore", { collection, ids: ids_arr });
                toast.success(`Restored ${rd.restored || 0} row${rd.restored === 1 ? "" : "s"}`);
                onDone && onDone();
              } catch (e) { toast.error(formatApiError(e)); }
            },
          },
        });
      }
      onDone && onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }, [selected, collection, clear, onDone, permanent, canBulk]);

  // -------------------- render helpers --------------------
  const RowCheckbox = useCallback(({ id, className = "" }) => {
    if (!canBulk) return null;
    return (
      <Checkbox
        checked={selected.has(id)}
        // Fully controlled by `selected` — the actual toggle (incl. Shift+Click
        // range-select) is decided in onClick below so we can read e.shiftKey,
        // which Radix's onCheckedChange does not expose.
        onCheckedChange={() => {}}
        className={className}
        aria-label={`Select row ${id}`}
        data-testid={`bulk-row-${id}`}
        onClick={(e) => { e.stopPropagation(); toggle(id, { shiftKey: e.shiftKey }); }}
      />
    );
  }, [canBulk, selected, toggle]);

  const HeaderCheckbox = useCallback(({ className = "" }) => {
    if (!canBulk) return null;
    return (
      <Checkbox
        checked={allSelected}
        onCheckedChange={toggleAll}
        className={className}
        aria-label="Select all rows"
        data-testid="bulk-header-checkbox"
        onClick={(e) => e.stopPropagation()}
      />
    );
  }, [canBulk, allSelected, toggleAll]);

  const ActionBar = useCallback(({ children } = {}) => {
    if (!canBulk || selected.size === 0) return null;
    return (
      <div
        className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-4 py-2.5 bg-slate-900 text-white rounded-full shadow-lg border border-slate-800"
        data-testid="bulk-action-bar"
      >
        <div className="text-sm font-medium" data-testid="bulk-selection-count">
          {selected.size} selected
        </div>
        <div className="w-px h-4 bg-slate-700" />
        {children}
        <Button
          size="sm"
          onClick={openConfirm}
          disabled={busy}
          className="bg-[#F54800] hover:bg-[#d63f00] text-white h-8 rounded-full"
          data-testid="bulk-delete-btn"
        >
          {busy ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <Trash2 size={13} className="mr-1.5" />}
          Delete Selected
        </Button>
        <button
          onClick={clear}
          className="text-slate-400 hover:text-white p-1 rounded-full"
          data-testid="bulk-clear-btn"
          aria-label="Clear selection"
        >
          <X size={14} />
        </button>
      </div>
    );
  }, [canBulk, selected.size, openConfirm, busy, clear]);

  const ConfirmDialog = useCallback(() => {
    if (!canBulk) return null;
    const count = selected.size;
    return (
      <AlertDialog open={confirmOpen} onOpenChange={(o) => { if (!busy) setConfirmOpen(o); }}>
        <AlertDialogContent className="rounded-sm" data-testid="bulk-delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Delete</AlertDialogTitle>
            <AlertDialogDescription>
              Delete {count} row{count === 1 ? "" : "s"}?{" "}
              {permanent
                ? "This is permanent and cannot be undone."
                : "You'll have 30 seconds to undo from a toast notification."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <label
            className="flex items-center gap-2 text-sm text-slate-700 px-1 py-1 cursor-pointer"
            data-testid="bulk-permanent-delete-label"
          >
            <Checkbox
              checked={permanent}
              onCheckedChange={(v) => setPermanent(!!v)}
              data-testid="bulk-permanent-delete-checkbox"
            />
            Permanently delete (cannot be undone)
          </label>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-delete-cancel-btn" disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); runDelete(); }}
              disabled={busy}
              className="rounded-sm bg-[#F54800] hover:bg-[#d63f00] text-white"
              data-testid="bulk-delete-confirm-btn"
            >
              {busy ? <Loader2 size={13} className="animate-spin mr-1.5" /> : null}
              {permanent ? "Delete Permanently" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }, [canBulk, confirmOpen, busy, selected.size, permanent, runDelete]);

  return {
    canBulk,
    selected,
    isSelected,
    toggle,
    clear,
    toggleAll,
    allSelected,
    RowCheckbox,
    HeaderCheckbox,
    ActionBar,
    ConfirmDialog,
  };
}

// Convenience — render a stateless "restore" toast when the caller wants
// to expose restore outside the auto-Undo window.
export function offerRestoreToast(collection, ids, onDone) {
  toast(`Restore ${ids.length} row${ids.length === 1 ? "" : "s"}?`, {
    action: {
      label: "Restore",
      onClick: async () => {
        try {
          const { data } = await api.post("/bulk-restore", { collection, ids });
          toast.success(`Restored ${data.restored || 0} row${data.restored === 1 ? "" : "s"}`);
          onDone && onDone();
        } catch (e) { toast.error(formatApiError(e)); }
      },
    },
    icon: <Undo2 size={14} />,
  });
}
